import { ref, computed } from 'vue';
import ApiClient from '@/ApiClient';

/**
 * Shared composable for division-based employee filtering.
 * Used by Open and Program award setup/create components to filter
 * approver and nominator dropdowns by selected divisions.
 *
 * @param {import('vue').Ref<string[]>} selectedDivisionIds - reactive ref of selected division UUIDs
 */
export function useDivisionEmployeeFiltering(selectedDivisionIds) {
  const employees = ref([]);
  const divisionEmployeePairs = ref([]);
  const employeesLoading = ref(false);
  const divisionEmployeePairsLoading = ref(true);
  const eligibleEmployeeIds = ref(new Set());

  const employeeOptions = computed(() => {
    const allEmployees = employees.value || [];
    const selectedDivisions = selectedDivisionIds.value || [];

    if (!selectedDivisions.length) {
      return allEmployees.map(e => ({
        text: `${e.full_name} - ${e.work_email}`,
        value: e.employee_id,
      }));
    }

    const allowed = eligibleEmployeeIds.value || new Set();
    return allEmployees
      .filter(e => allowed.has(e.employee_id))
      .map(e => ({
        text: `${e.full_name} - ${e.work_email}`,
        value: e.employee_id,
      }));
  });

  // All company employees, unfiltered by division. Used by the approver dropdown,
  // which can be drawn from anyone in the company rather than just in-scope employees.
  const allEmployeeOptions = computed(() =>
    (employees.value || []).map(e => ({
      text: `${e.full_name} - ${e.work_email}`,
      value: e.employee_id,
    })),
  );

  const refreshEligibleEmployeeIds = () => {
    const ids = selectedDivisionIds.value || [];
    const pairs = divisionEmployeePairs.value || [];
    if (!ids.length || !pairs.length) {
      eligibleEmployeeIds.value = new Set((employees.value || []).map(e => e.employee_id));
      return;
    }
    const selectedSet = new Set(ids.map(String));
    const union = new Set();
    for (const { employee_id, division_id } of pairs) {
      if (selectedSet.has(String(division_id))) {
        union.add(employee_id);
      }
    }
    eligibleEmployeeIds.value = union;
  };

  const loadEmployeesAndDivisionPairs = () => [
    (async () => {
      try {
        employeesLoading.value = true;
        employees.value = (await ApiClient.listRecognitionEmployees())?.data || [];
      } catch {
        // silently ignore
      } finally {
        employeesLoading.value = false;
      }
    })(),
    (async () => {
      try {
        divisionEmployeePairs.value = (await ApiClient.getEmployeeDivisionInfo())?.data || [];
      } catch {
        // silently ignore — approver filtering falls back to all employees
      } finally {
        divisionEmployeePairsLoading.value = false;
      }
    })(),
  ];

  return {
    employees,
    divisionEmployeePairs,
    employeesLoading,
    divisionEmployeePairsLoading,
    eligibleEmployeeIds,
    employeeOptions,
    allEmployeeOptions,
    refreshEligibleEmployeeIds,
    loadEmployeesAndDivisionPairs,
  };
}
